import { create, tsx } from '@dojo/framework/core/vdom';
import { Head } from '../../widgets/Head';
import { Hero } from '../../widgets/Hero';
import * as css from './CaseStudy.m.css';
import { Summary } from '../../widgets/Summary';
import ImageSection from '../../widgets/ImageSection';
import { Link } from '@dojo/framework/routing/Link';
import ContentWrapper from '../../widgets/ContentWrapper';
import SpeechBubble from '../../widgets/SpeechBubble';
import Technologies from '../../widgets/Technologies';
import Section from '../../widgets/Section';
import ConnectStrip from '../../widgets/ConnectStrip';
import { Text } from '../../widgets/Text';
import Image from '../../widgets/Image';

const marriottMain = require('../../assets/images/work-mockups/marriott-mockup.png');
const marriottMainHighRes = require('../../assets/images/work-mockups/marriott-mockup@2x.png');
const marriottBottomLeft = require('../../assets/images/work-mockups/marriot-card-bottom-left.png');
const marriottBottomLeftHighRes = require('../../assets/images/work-mockups/marriot-card-bottom-left@2x.png');
const marriottBottomRight = require('../../assets/images/work-mockups/marriot-card-bottom-right.png');
const marriottBottomRightHighRes = require('../../assets/images/work-mockups/marriot-card-bottom-right@2x.png');

const factory = create();

export const CaseStudyMarriott = factory(function CaseStudyMarriott() {
	return (
		<div classes={css.root}>
			<Head
				title="Modernizing Apps &amp; Improving Usability for Marriott | SitePen"
				description="See how our team at SitePen provided technical expertise and leadership to help Marriott forge a new path in updating its business applications."
			/>
			<Hero background="level1RedOrange" align="center" paddingBottom="sm">
				{{
					title: 'Case Study',
					xlTitle: 'Marriott',
					blurb: (
						<virtual>
							<span>
								Growth for a company often comes with change, which can provide its
								own set of challenges. Providing a stable enterprise application,
								aligning with new technologies, rolling out new features to meet the
								needs of the business, and giving users a consistent experience are
								often competing priorities for the enterprise. What’s the best way
								to maintain productivity and usability as an application grows well
								beyond its original mission?
							</span>
							<Technologies
								icons={['dojo-toolkit', 'dgrid', 'dstore', 'intern', 'javascript']}
							></Technologies>
						</virtual>
					)
				}}
			</Hero>
			<ContentWrapper size="small">
				<Text type="section">The Situation</Text>
				<Text type="body">
					Marriott was an early adopter of web-based technologies, having built many
					critical business applications that leveraged early technologies such as Java
					applets and Java Server Pages (JSPs). These technologies, however, eventually
					impeded its ability to iterate and provide a modern user experience. As more
					users came to rely on these systems, the effort required to maintain and grow
					the applications became increasingly difficult to manage, blocking high priority
					business objectives.
				</Text>
			</ContentWrapper>
			<ImageSection shaded paddingTop="lg" contentBias>
				{{
					title: 'The Solution',
					blurb: (
						<div classes={[css.stackedBlurb]}>
							<Text type="body">
								SitePen provided senior technical{' '}
								<Link to="expertise">expertise</Link> and leadership to help
								Marriott forge a new path in developing its mission-critical
								applications. SitePen migrated several applications to new
								interfaces and technologies, which maximized both developer and user
								productivity and usability. These new application interfaces were
								designed to integrate with the existing infrastructure to ensure
								that critical business logic was retained throughout.
							</Text>
						</div>
					),
					images: {
						main: (
							<div classes={[css.stackedMainImage]}>
								<Image
									src={marriottMain}
									highResSrc={marriottMainHighRes}
									shadow="light"
									responsive={{
										medium: { shadow: 'dark' }
									}}
								/>
							</div>
						),
						bottom: {
							left: (
								<Image
									src={marriottBottomLeft}
									highResSrc={marriottBottomLeftHighRes}
									shadow="dark"
									size="lg"
								/>
							),
							right: (
								<Image
									src={marriottBottomRight}
									highResSrc={marriottBottomRightHighRes}
									shadow="dark"
									size="lg"
								/>
							)
						}
					}
				}}
			</ImageSection>
			<Section type="leading">
				<ContentWrapper>
					<Summary blurbGutter="medium" imageGutter="none">
						{{
							title: 'The Result',
							blurb: (
								<virtual>
									<p>
										With SitePen’s <Link to="about">leadership</Link>,
										knowledge, and ability to meet complex requirements within
										estimates, Marriott created maintainable and extensible
										business applications that satisfy all stakeholder needs.
										The platform is much easier to use and removes silos between
										business processes, leading to more actionable insights and
										increased team productivity.
									</p>
									<p>
										With an updated front-end platform, Marriott is able to
										continuously modernize their data-heavy applications to
										improve usability and sustainability over time. Due to the
										streamlined workflows resulting from the new system,
										Marriott can increase focus on maximizing revenue and
										achieving business objectives.
									</p>
								</virtual>
							),
							image: (
								<SpeechBubble>
									{{
										main:
											'SitePen’s expertise and quality of work is consistently exceptional. They are a trusted and valued partner.',
										subtitle1: 'Sr. Software Engineer',
										subtitle2: 'Marriott International'
									}}
								</SpeechBubble>
							)
						}}
					</Summary>
				</ContentWrapper>
			</Section>
			<Section type="trailing" shaded>
				<ConnectStrip trailing />
			</Section>
		</div>
	);
});

export default CaseStudyMarriott;
